import { useState } from "react";
import { Link } from "react-router-dom";
import { Boxes, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { apiError } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const AuthShell = ({ title, subtitle, children, footer }) => {
  const { t } = useI18n();
  return (
  <div className="grid min-h-screen lg:grid-cols-[1fr_1.1fr]">
    <div className="flex items-center justify-center px-6 py-14">
      <div className="w-full max-w-sm rise">
        <div className="mb-9 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-900">
            <Boxes className="h-4 w-4 text-white" />
          </span>
          <div>
            <span className="block text-xl font-extrabold leading-none tracking-tight">Operativo AI</span>
            <span className="mt-1 block text-xs font-semibold text-slate-500">
              {t("brand.tagline")}
            </span>
          </div>
          </div>
          <LanguageSwitcher />
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight">{title}</h1>
        <p className="mt-2 text-sm text-slate-500">{subtitle}</p>
        <div className="mt-8">{children}</div>
        <div className="mt-6 text-sm text-slate-500">{footer}</div>
      </div>
    </div>
    <div className="relative hidden overflow-hidden bg-slate-950 lg:block grain">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(79,70,229,0.35),transparent_55%)]" />
      <div className="relative flex h-full flex-col justify-end p-14 text-slate-200">
        <p className="max-w-md text-2xl font-extrabold leading-snug tracking-tight text-white">
          {t("auth.heroTitle")}
        </p>
        <p className="mt-4 max-w-md text-sm text-slate-400">{t("auth.heroText")}</p>
      </div>
    </div>
  </div>
  );
};

const Login = () => {
  const { login } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(form.email, form.password);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={t("login.title")}
      subtitle={t("login.subtitle")}
      footer={
        <>
          {t("login.noAccount")}{" "}
          <Link to="/registrati" data-testid="go-register" className="font-semibold text-blue-600 hover:underline">
            {t("login.register")}
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="email">{t("common.email")}</Label>
          <Input
            id="email"
            data-testid="login-email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            className="mt-1.5 focus:ring-2 focus:ring-blue-600"
          />
        </div>
        <div>
          <Label htmlFor="password">{t("login.password")}</Label>
          <Input
            id="password"
            data-testid="login-password"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
            className="mt-1.5 focus:ring-2 focus:ring-blue-600"
          />
        </div>
        {error && (
          <p data-testid="login-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}
        <Button
          data-testid="login-submit"
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white hover:bg-blue-700"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("login.submit")}
        </Button>
      </form>
    </AuthShell>
  );
};

export { AuthShell };
export default Login;
